import Analytics from '@opencare/analytics';
import React, { useCallback, useEffect, useMemo } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { Loading } from 'common/components/Loading/Loading';

import { ModalHeader, ModalSubtitle, ModalTitle } from 'common/components/Modal/Modal-styled-components';

import { updateSelectedAppointment } from 'connect/actions';
import { selectDataClinic, selectDataClinicLoading } from 'connect/selectors';
import { CheckoutStateHandler } from 'pages/checkout/connect/state-handlers';
import { CheckoutState } from 'pages/checkout/connect/types';

import { filterInNetworkProviders } from 'pages/profile/helpers/profile-helpers';

import { Clinic } from 'types/Clinic';
import Logger from 'utils/Logger';

import { NoAvailabilityModalBody, StyledInstantBookView, StyledNoAvailabilityModal } from './styled-components';

const analytics = new Analytics();
const modalAnalyticsConstants = {
  pageType: analytics.pageTypes.CHECKOUT,
  pageName: 'checkout-confirm',
  name: 'no-availability-modal',
  section: 'no-availability-modal',
};

type NoAvailabilityModalProps = {
  isOpen: boolean;
  onClose: () => void;
};

export const NoAvailabilityModal = ({
  isOpen,
  onClose,
}: NoAvailabilityModalProps) => {
  const dispatch = useDispatch();

  const checkoutState = useSelector<any, CheckoutState>(CheckoutStateHandler.selector);
  const insurancePlan = checkoutState?.insurance?.plan;

  const clinic: Clinic = useSelector(selectDataClinic);
  const isClinicLoading = useSelector(selectDataClinicLoading);

  // Slots from in-network providers
  const availableSlots = useMemo(() => {
    // If insurancePlan is falsey, use all slots
    // This matches the behaviour used by filterInNetworkProviders
    if (!insurancePlan?.id) {
      return clinic.slots;
    }

    const inNetworkProviders = filterInNetworkProviders(clinic.hours, insurancePlan?.id, clinic.providers);
    const inNetworkProviderIds = inNetworkProviders.map((provider) => provider.id);

    return clinic.slots.filter(
      (slot) => {
        const providerId = typeof slot.provider === 'number' ? slot.provider : slot.provider.id;
        return inNetworkProviderIds.includes(providerId);
      },
    );
  }, [clinic.hours, clinic.providers, clinic.slots, insurancePlan?.id]);

  const handleSelectSlot = useCallback((slotId: number) => {
    if (!clinic.providers?.length) {
      Logger.error('NoAvailabilityModal: No providers found, cannot update timeslot.', { providers: clinic.providers });
      return;
    }

    const requestedProviderId = clinic.slots.find((slot) => slot.id === slotId)?.provider;
    const requestedProvider = clinic.providers.find((provider) => provider.id === requestedProviderId);

    dispatch(
      updateSelectedAppointment(
        requestedProviderId,
        slotId,
        requestedProvider,
        true, // areAllProvidersSelected,
      ),
    );

    analytics.trackClick({
      ...modalAnalyticsConstants,
      customProperties: {
        slot_id: String(slotId),
      },
    });

    onClose();
  }, [onClose, clinic.slots, clinic.providers, dispatch]);

  // Track a pageview whenever this modal is opened, e.g. because a live slot-availability check found the slot unavailable
  useEffect(() => {
    if (!isOpen) {
      return;
    }

    analytics.trackPageView(modalAnalyticsConstants);
  }, [isOpen]);

  const instantBookView = useMemo(() => (
    <StyledInstantBookView
      allSlots={availableSlots}
      onRequestAppointment={handleSelectSlot}
      numDaysToDisplay={7}
      showDateRange
      showSelectedDate
    />
  ), [availableSlots, handleSelectSlot]);

  return (
    <StyledNoAvailabilityModal
      visible={isOpen}
      onClose={onClose}
    >
      <NoAvailabilityModalBody data-testid="no-availability-modal">
        <ModalHeader>
          <ModalTitle>
            Your selected time is no longer available
          </ModalTitle>
          <ModalSubtitle>
            Please choose another available slot to continue your appointment booking.
          </ModalSubtitle>
        </ModalHeader>

        {isClinicLoading ? <Loading /> : instantBookView}
      </NoAvailabilityModalBody>
    </StyledNoAvailabilityModal>
  );
};
